/**
 * useSessionLifecycle (Welle 6f)
 *
 * Wird in App.tsx aufgerufen. Verwaltet zwei Timer pro Login:
 *  - Warn-Timer: 5 Minuten vor Token-Ablauf → setShowExpiryWarning(true)
 *  - Logout-Timer: bei Token-Ablauf → clearToken() + view → 'login'
 *
 * Re-arm bei Login (wenn ein neues Token kommt) via 'storage'-Event auf TOKEN_KEY.
 */

import { useEffect, useState } from 'react';
import { useUIStore } from '@/state/store';
import { clearToken, getMsUntilExpiry, isTokenValid } from '@/lib/auth';

const WARNING_MS_BEFORE_EXPIRY = 5 * 60 * 1000; // 5 Minuten

export function useSessionLifecycle() {
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const setView = useUIStore((s) => s.setView);

  useEffect(() => {
    let warnTimer: ReturnType<typeof setTimeout> | null = null;
    let logoutTimer: ReturnType<typeof setTimeout> | null = null;

    function armTimers() {
      if (warnTimer) clearTimeout(warnTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
      setShowExpiryWarning(false);

      const ms = getMsUntilExpiry();
      if (ms === null) return; // kein Token, nichts zu tun
      if (ms <= 0) {
        // Token bereits abgelaufen
        console.log('[session] Token bereits abgelaufen beim Laden');
        clearToken();
        setView('login');
        return;
      }

      console.log(`[session] Token gültig noch ${Math.round(ms / 1000)}s, arme Timer`);

      // Warnung 5 Minuten vor Ablauf (oder sofort wenn weniger Zeit übrig)
      const warnIn = Math.max(0, ms - WARNING_MS_BEFORE_EXPIRY);
      warnTimer = setTimeout(() => {
        console.log('[session] Warnung: Token läuft in 5 Minuten ab');
        setShowExpiryWarning(true);
      }, warnIn);

      // Harter Logout bei Ablauf
      logoutTimer = setTimeout(() => {
        console.log('[session] Token abgelaufen, Auto-Logout');
        clearToken();
        setView('login');
      }, ms);
    }

    armTimers();

    // Bei Storage-Änderung (z.B. Login in anderem Tab oder lokal nach Logout+Login)
    function onStorage(e: StorageEvent) {
      if (e.key === 'bamoa_token') armTimers();
    }
    // Bei Custom-Logout-Event (z.B. apiFetch hat 401 abgefangen)
    function onLogout() {
      setShowExpiryWarning(false);
      if (warnTimer) clearTimeout(warnTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
      setView('login');
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener('bamoa:logout', onLogout);

    return () => {
      if (warnTimer) clearTimeout(warnTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('bamoa:logout', onLogout);
    };
  }, [setView]);

  return {
    showExpiryWarning,
    dismissWarning: () => setShowExpiryWarning(false),
    isValid: isTokenValid,
  };
}
